import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  useGmailStatus,
  useGmailDisconnect,
  useGmailTest,
  gmailConnect,
} from '../../../data/support/useGmail';

const GmailSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusQuery = useGmailStatus();
  const disconnectMutation = useGmailDisconnect();
  const testMutation = useGmailTest();
  const [connecting, setConnecting] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  // Handle OAuth callback redirect params
  React.useEffect(() => {
    const gmailParam = searchParams.get('gmail');
    if (gmailParam === 'connected') {
      setFeedback({
        severity: 'success',
        message: 'Gmail account connected successfully.',
      });
      searchParams.delete('gmail');
      setSearchParams(searchParams, { replace: true });
    } else if (gmailParam === 'error') {
      setFeedback({
        severity: 'error',
        message: 'Failed to connect Gmail account. Please try again.',
      });
      searchParams.delete('gmail');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await gmailConnect();
    } catch {
      setFeedback({
        severity: 'error',
        message: 'Failed to start Gmail connection. Please try again.',
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setFeedback(null);
    try {
      await disconnectMutation.mutateAsync();
      setFeedback({
        severity: 'success',
        message: 'Gmail account disconnected.',
      });
    } catch {
      setFeedback({
        severity: 'error',
        message: 'Failed to disconnect Gmail account.',
      });
    }
  };

  const handleTest = async () => {
    setFeedback(null);
    try {
      await testMutation.mutateAsync();
      setFeedback({
        severity: 'success',
        message: 'Test email sent successfully.',
      });
    } catch {
      setFeedback({
        severity: 'error',
        message: 'Failed to send test email.',
      });
    }
  };

  const status = statusQuery.data;
  const isLoading = statusQuery.isLoading;

  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <Typography>Loading Gmail settings...</Typography>
        </Box>
      </Paper>
    );
  }

  const watchExpiry = status?.watch_expiry
    ? new Date(status.watch_expiry)
    : null;
  const watchActive = watchExpiry ? watchExpiry > new Date() : false;

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Gmail Integration
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback(null)}
          sx={{ mb: 2 }}
        >
          {feedback.message}
        </Alert>
      )}

      {!status?.connected ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Gmail account to send case replies from your email
            address.
          </Typography>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={connecting}
            startIcon={connecting ? <CircularProgress size={16} /> : undefined}
          >
            {connecting ? 'Connecting...' : 'Connect Gmail'}
          </Button>
        </>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Connected as:</strong> {status.gmail_address}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Watch status:</strong>{' '}
              {watchActive ? 'Active' : 'Expired'}
              {watchExpiry && ` (expires ${watchExpiry.toLocaleDateString()})`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? 'Sending...' : 'Send Test Email'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
            >
              {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default GmailSettings;
